/**
 * Typed access to the versioned static curriculum.
 *
 * Content is exported from the backend seed by scripts/export-static-curriculum.mjs
 * (see frontend/src/data/curriculum.meta.json for the inventory and source hash).
 * This module is the single in-app source for tracks, exercises, and stories;
 * nothing fetches curriculum content over the network.
 */

import curriculumData from './curriculum.json';

export interface StaticExerciseContent {
  schemaVersion: number;
  promptText: string;
  payload: Record<string, unknown>;
}

export interface StaticExercise {
  trackExerciseId: string;
  exerciseSlug: string;
  sequenceNo: number;
  titleOverride: string | null;
  moduleKey: string | null;
  /** Present on legacy-seed lessons: the original numeric lessons-table id. */
  legacyLessonId?: number;
  /** Per-lesson keyboard layout (legacy seed); defaults to the track layout. */
  keyboardLayoutKey?: string;
  /** False when the lesson's layout has no practice geometry (GH #110). */
  practiceSupported?: boolean;
  /** Original legacy track label preserved by the catalog consolidation. */
  sourceTrackKey?: string;
  exercise: {
    exerciseKey: string;
    exerciseType: string;
    title: string;
    skillFocus: string | null;
    requiredAccuracyThreshold: number;
    targetWpm: number;
  };
  content: StaticExerciseContent;
}

export interface StaticTrack {
  trackKey: string;
  trackId: string;
  title: string;
  languageCode: string;
  scriptCode: string;
  localeCode: string;
  keyboardLayoutKey: string;
  difficultyBand: string;
  isDefaultForContext: boolean;
  modules: Array<{ moduleKey: string; title: string; sequenceNo: number }>;
  exercises: StaticExercise[];
}

export interface StaticStory {
  id: number;
  storySlug: string;
  title: string;
  source: string;
  hindiContent: string;
  difficultyLevel: string;
  wordCount: number;
  estimatedCompletionSeconds: number;
  tags: string[];
}

export const CURRICULUM_DATA_VERSION: string = curriculumData.dataVersion;

export const tracks: StaticTrack[] = curriculumData.tracks;

export const stories: StaticStory[] = curriculumData.stories;

export function findTrack(trackKey: string): StaticTrack | undefined {
  return tracks.find((track) => track.trackKey === trackKey);
}

export function findExercise(
  trackKey: string,
  exerciseSlug: string,
): StaticExercise | undefined {
  return findTrack(trackKey)?.exercises.find(
    (exercise) => exercise.exerciseSlug === exerciseSlug,
  );
}

/** The track a fresh visitor lands on: the seeded default for the context. */
export function defaultTrack(): StaticTrack {
  const seededDefault = tracks.find((track) => track.isDefaultForContext);
  return seededDefault ?? tracks[0];
}

export function exerciseKey(trackKey: string, exerciseSlug: string): string {
  return `${trackKey}/${exerciseSlug}`;
}

export function allExerciseKeys(): string[] {
  return tracks.flatMap((track) =>
    track.exercises.map((exercise) => exerciseKey(track.trackKey, exercise.exerciseSlug)),
  );
}

/** Expected practice text for an exercise (schema v1: payload.expectedText). */
export function expectedTextFor(exercise: StaticExercise): string {
  const value = exercise.content.payload.expectedText;
  return typeof value === 'string' ? value : '';
}

export function nextExerciseSlug(
  trackKey: string,
  exerciseSlug: string,
): string | null {
  const track = findTrack(trackKey);
  if (!track) return null;
  const index = track.exercises.findIndex(
    (exercise) => exercise.exerciseSlug === exerciseSlug,
  );
  if (index < 0 || index + 1 >= track.exercises.length) return null;
  return track.exercises[index + 1].exerciseSlug;
}
